import { useCallback, useState } from 'react'
import { saveAs } from 'utils/saveAs'
import { useStoTrack } from './useStoTracking'
import { fetchFile, fetchFilePublic } from 'api/fetch-file'

interface DownloadArgs {
  id: string
  fileName: string
  isPublic: boolean
  /** The document's tab (type), for analytics. */
  group?: string
  /** `preview` (the card) or `drawer` (the full list), for analytics. */
  location?: string
}

/**
 * Documents are not plain links — the file is fetched as a blob and handed to the
 * browser, so the request can carry auth. A publicly downloadable document always uses
 * the anonymous endpoint; everything else uses the authenticated one.
 *
 * Delivered via `saveAs` (an <a download> click), not window.open, which browsers block because the user gesture has already expired by the
 * time the blob arrives. A download button should download, not open a tab, anyway.
 */
export function useDocumentDownload() {
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [failedId, setFailedId] = useState<string | null>(null)
  const trackSto = useStoTrack()

  const download = useCallback(
    async ({ id, fileName, isPublic, group, location }: DownloadArgs) => {
      if (!id || downloadingId) return

      setDownloadingId(id)
      setFailedId(null)

      // Never throws: analytics cannot stop a download.
      trackSto('sto_document_downloaded', { file_name: fileName, doc_type: group, location })

      try {
        const response = isPublic
          ? await fetchFilePublic('documents/public/getfile', { documentId: id })
          : await fetchFile('documents/getfile', { documentId: id })

        saveAs(new Blob([response.data], { type: 'application/pdf' }), fileName)
      } catch (error) {
        // Without this the button simply re-enables and the failure is invisible.
        console.error('[sto/documents] download failed', fileName, error)
        setFailedId(id)
        trackSto('sto_document_download_failed', { file_name: fileName, doc_type: group, location })
      } finally {
        setDownloadingId(null)
      }
    },
    [downloadingId, trackSto],
  )

  return { download, downloadingId, failedId }
}
